import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import Avatar from '../common/Avatar';
import Icon from '../common/Icon';
import ThemeToggle from '../common/ThemeToggle';
import HelpModal from '../help/HelpModal';
import NotificationBell from '../layout/NotificationBell';
import UserMenu from '../layout/UserMenu';
import { useAuth } from '../../hooks/useAuth';

// NavLink is active for both an exact match and any descendant path by
// default (no `end` prop) - so /admin/users/42 already keeps "Users"
// active with zero extra prefix-matching code.
const ADMIN_NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Overview', icon: 'dashboard' },
  { to: '/admin/users', label: 'Users', icon: 'user' },
  { to: '/admin/meetings', label: 'Meetings', icon: 'meeting' },
  { to: '/admin/pricing', label: 'Pricing', icon: 'zap' },
  { to: '/admin/billing', label: 'Billing', icon: 'receipt' },
  { to: '/admin/system', label: 'System', icon: 'settings' },
  { to: '/admin/release-emails', label: 'Release Emails', icon: 'bell' },
  { to: '/admin/audit', label: 'Audit Log', icon: 'audit' },
];

function AdminShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const closeNav = () => setIsNavOpen(false);

  // Close admin drawer on route change
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  // Close admin drawer on Escape key
  useEffect(() => {
    if (!isNavOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsNavOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isNavOpen]);

  // Lock body scroll while admin drawer is open on mobile
  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isNavOpen]);

  const handleAdminSearch = (event) => {
    event.preventDefault();
    navigate('/admin/meetings');
  };

  return (
    <div className="app-shell admin-shell">
      <aside
        id="admin-navigation-drawer"
        className={`sidebar admin-sidebar ${isNavOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal={isNavOpen ? 'true' : 'false'}
        aria-label="Admin navigation"
      >
        <div className="sidebar-brand admin-brand">
          <span className="brand-mark">
            <Icon name="shield" size={21} />
          </span>
          <span className="brand-copy">
            <span className="brand-name">KrishvaQ</span>
            <span className="brand-subtitle">Admin Intelligence</span>
          </span>
          <span className="admin-badge">Admin</span>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-nav-group">
            <div className="sidebar-nav-label">Control Center</div>
            <div className="sidebar-nav-list">
              {ADMIN_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `sidebar-nav-link ${isActive ? 'active' : ''}`
                  }
                  onClick={closeNav}
                >
                  <span className="sidebar-nav-icon">
                    <Icon name={item.icon} />
                  </span>
                  <span className="sidebar-nav-text">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        <Link to="/app/dashboard" className="sidebar-back-link" onClick={closeNav}>
          <Icon name="home" size={16} />
          <span>Back to Workspace</span>
        </Link>

        {user && (
          <div className="sidebar-footer">
            <div className="sidebar-user-card sidebar-user-card-static">
              <Avatar user={user} className="avatar" />
              <span>
                <span className="sidebar-footer-name">{user.full_name}</span>
                <span className="sidebar-footer-hint">{user.email}</span>
                <span className="admin-badge">Admin</span>
              </span>
            </div>
          </div>
        )}
      </aside>

      {isNavOpen && (
        <button
          type="button"
          className="admin-sidebar-overlay"
          aria-label="Close admin navigation"
          onClick={closeNav}
        />
      )}

      <div className="app-main">
        <header className="topbar admin-topbar">
          <button
            type="button"
            className="admin-nav-toggle"
            aria-label={isNavOpen ? 'Close admin navigation' : 'Open admin navigation'}
            aria-expanded={isNavOpen}
            aria-controls="admin-navigation-drawer"
            onClick={() => setIsNavOpen((open) => !open)}
          >
            <Icon name={isNavOpen ? 'close' : 'menu'} />
          </button>
          <form className="global-search" role="search" onSubmit={handleAdminSearch}>
            <Icon name="search" size={17} />
            <input
              type="search"
              aria-label="Admin search"
              placeholder="Search meetings, transcripts, insights..."
            />
            <div className="global-search-scopes" aria-hidden="true">
              <span>Users</span>
              <span>Meetings</span>
              <span>Audit</span>
            </div>
          </form>
          <div className="topbar-actions">
            <ThemeToggle />
            <NotificationBell />
            <button
              type="button"
              className="topbar-help-button"
              aria-haspopup="dialog"
              aria-expanded={isHelpOpen}
              onClick={() => setIsHelpOpen(true)}
            >
              <Icon name="help" />
              <span>Help</span>
            </button>
            <UserMenu />
          </div>
        </header>

        {isHelpOpen && <HelpModal onClose={() => setIsHelpOpen(false)} />}

        <main className="content-area">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AdminShell;
